import { Patient } from 'src/models';
import { SearchedPatient, useGetPatientList } from 'src/hooks/patient';
import { Select, SelectProps } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { formatStaticDate } from 'src/utils';
import { useDebouncedValue } from 'src/hooks/misc';
import { classNames } from 'src/utilities';

type PatientOption = {
  label: SearchedPatient['patient_Name'];
  value: SearchedPatient['patient_id'];
} & SearchedPatient;

type PatientDropdownProps = {
  selectProps?: SelectProps;
  onChange: (
    patient: SearchedPatient['patient_id'],
    option?: PatientOption | PatientOption[]
  ) => void;
  value: Patient['patient_id'] | undefined;
};

const PatientDropdown = ({
  selectProps,
  onChange,
  value,
}: PatientDropdownProps) => {
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isLoading, isFetching } = useGetPatientList({
    search: debouncedSearch,
  });

  const notFoundContent = useMemo(() => {
    if (isLoading) {
      return 'Searching...';
    }

    if (debouncedSearch.length) {
      return 'No patients found';
    }
    return 'Search using patient name or Id';
  }, [debouncedSearch, isLoading]);

  return (
    <Select
      showSearch
      allowClear
      {...selectProps}
      value={value}
      className={classNames(
        'min-w-[200px] font-normal',
        selectProps?.className
      )}
      placeholder="Search patient"
      prefix={
        isLoading || isFetching ? (
          <LoadingOutlined className="text-gray-400" />
        ) : (
          <SearchOutlined className="text-gray-400" />
        )
      }
      notFoundContent={notFoundContent}
      optionRender={(patient) => {
        const { patient_Name, patient_id, patient_birth_date } =
          patient?.data || {};
        const name = (patient_Name || '').trim();
        return (
          <div className="flex flex-col">
            {name ? (
              <div className="font-medium">{name}</div>
            ) : (
              <div className="text-gray-500 italic">Name unknown</div>
            )}
            <div className="text-sm text-gray-500">
              {patient_id} • {formatStaticDate(patient_birth_date)}
            </div>
          </div>
        );
      }}
      filterOption={() => {
        // Filtering is already taken care by the useGetPatientList hook
        return true;
      }}
      onSearch={setSearch}
      options={data?.patients?.map((patient) => ({
        label: patient.patient_Name,
        value: patient.patient_id,
        ...patient,
      }))}
      onChange={onChange}
    />
  );
};

export { PatientDropdown };
